import { Typography } from '@remarkable/ark-web';

import type { useCart } from 'src/api/queries';
import { Skeleton } from 'src/components';
import { createContentSwitch } from 'src/utils/createContentSwitch';
import { formatDate } from 'src/utils/formatDate';
import { formatAmount } from 'src/utils/productUtils';

import { Trans, useLingui } from '@lingui/react/macro';
import { cartHelpers } from 'src/utils/cartHelpers';
import { useFreeTrialLengthText } from 'src/utils/freeTrialLength';
import { Price } from './Price';

export const CartItemConnectWithTrial = ({
  cart,
}: {
  cart: ReturnType<typeof useCart>;
}) => {
  const { t, i18n } = useLingui();
  const showOnCheckoutType = createContentSwitch(cart.checkoutType);
  const updatingPrice =
    cart.updateCurrency.isPending ||
    cart.updateTax.isPending ||
    cart.setup.isPending;

  const freeTrialLengthDays = cart.setup.data?.freeTrialLengthDays;
  const freeTrialLengthText = useFreeTrialLengthText(freeTrialLengthDays ?? 0);
  const freeTrialLengthDisplay = freeTrialLengthDays
    ? freeTrialLengthText
    : t`Free trial`;
  const connectOfferBillingDate = cartHelpers.formatBillingStartDate(
    cart.details.data,
    i18n.locale
  );
  const winbackOfferBillingDate = formatDate(
    cart.details.data?.data.billingDate ?? '',
    { locale: i18n.locale }
  );
  return (
    <>
      <div data-cy="connect-plan-row" className="flex justify-between">
        <p className="my-4 font-semibold">
          <Skeleton isLoading={cart.setup.isPending}>
            {freeTrialLengthDisplay}
          </Skeleton>
        </p>
        <p className="my-4">
          <b>
            <Skeleton isLoading={updatingPrice}>
              <Typography variant="body-md">
                {formatAmount(0, cart.details.data?.data.currency, i18n.locale)}
              </Typography>
            </Skeleton>
          </b>
        </p>
      </div>
      <div className="flex justify-between" data-cy="billing-starts-row">
        <p data-cy="billing-start-date" className="my-4">
          {showOnCheckoutType({
            ['connectOffer']: (
              <Trans>Billing starts&nbsp;{connectOfferBillingDate}</Trans>
            ),
            ['winbackOffer']: (
              <Trans>
                Billing starts&nbsp;
                <Skeleton isLoading={updatingPrice}>
                  {winbackOfferBillingDate}
                </Skeleton>
              </Trans>
            ),
            ['dmOffer']: t`We’ll send you a reminder email before your trial ends.`,
          })}
        </p>
        <p className="my-4">
          <Skeleton isLoading={updatingPrice}>
            <Price cart={cart} showTaxStatus />
          </Skeleton>
        </p>
      </div>
      <Typography variant="body-sm" className="mt-16">
        {showOnCheckoutType({
          ['connectOffer']: t`We’ll send you a reminder email before your free trial ends.`,
          ['winbackOffer']: t`We’ll send you a reminder email before your free trial ends.`,
        })}
      </Typography>
    </>
  );
};
